import * as React from 'react'

import { Alert, Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  AddCandidatesToJobPostingDocument,
  AddToJobPostingDataDocument,
  AtsAvailableJobPostingsDocument,
  AtsCreatedApplicationsfromCandidatesFragment,
} from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

const EMPTY_JOB_POSTING = -1

interface AddToJobPostingForm {
  atsJobPostingId: number
  atsApplicationPhaseId: number | null
}

interface Props {
  candidateIds: number[]
  onClose: () => void
  onSuccess: (applications: AtsCreatedApplicationsfromCandidatesFragment[]) => void
}

export const AddCandidatesToJobPostingModal = ({ candidateIds, onClose, onSuccess }: Props) => {
  const [addToJobPosting] = useMutation(AddCandidatesToJobPostingDocument, {
    extractorFn: (data) => data.atsMutations.createFromCandidatesAtsApplication,
    refetchQueries: ['AtsCandidatesDataCollection'],
  })

  const form = useForm<AddToJobPostingForm>({
    defaultValues: {
      atsJobPostingId: EMPTY_JOB_POSTING,
      atsApplicationPhaseId: null,
    },
    validate(values) {
      const errors: FormErrors<AddToJobPostingForm> = {}

      if (values.atsJobPostingId < 1) {
        errors.atsJobPostingId = i18n.t('common.validations.empty')
      }

      if (values.atsJobPostingId > 0 && !values.atsApplicationPhaseId) {
        errors.atsApplicationPhaseId = i18n.t('common.validations.empty')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await addToJobPosting({
        variables: { atsCandidateIds: candidateIds, ...values },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success) {
        const applications = handled.data.applications ?? []

        onSuccess(applications)

        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.candidates.add_to_another_job_posting.success'),
        })

        track(trackingEvents.ats.candidateAddedToJobPosting)
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('ats.candidates.add_to_another_job_posting.error'),
        })
      }

      return handled
    },
  })
  const { data: jobsQuery, loading: jobsLoading } = useQuery(AtsAvailableJobPostingsDocument)
  const { data: jobData, loading: jobDataLoading } = useQuery(AddToJobPostingDataDocument, {
    variables: { atsJobPostingId: form.values.atsJobPostingId, atsCandidateIds: candidateIds },
    skip: form.values.atsJobPostingId === EMPTY_JOB_POSTING,
  })

  const jobs = jobsQuery?.ats.jobPostings || []
  const applicationPhases = jobData?.ats.applicationPhasesConnection.nodes || []
  const hiringStages = jobData?.ats.hiringStagesConnection.nodes || []
  const candidatesAlreadyInTheJobPosting = jobData?.ats.applicationsConnection.totalCount || 0

  const selectedJobPosting = jobs.find((job) => job.id === form.values.atsJobPostingId)

  const createOrderedOptions = () =>
    hiringStages.flatMap((hiringStage) => {
      const phasesInStage = sortBy(
        applicationPhases.filter((phase) => phase.hiringStage?.id === hiringStage.id),
        'position'
      )

      if (phasesInStage.length > 0) {
        return [
          {
            label: `-- ${hiringStage.label} --`,
            value: hiringStage.id,
            disabled: true,
          },
          ...phasesInStage.map((phase) => ({
            label: phase.name,
            value: phase.id,
          })),
        ]
      }
      return []
    })

  return (
    <Modal
      illustration="user-move"
      title={i18n.t('ats.candidates.add_to_another_job_posting.title')}
      description={i18n.t('ats.candidates.add_to_another_job_posting.description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button disabled={false} submit>
          {i18n.t('ats.candidates.add_to_another_job_posting.add_to_job_opening')}
        </Button>
      }
    >
      <Box gap="s16">
        <DropdownSearchBar
          name={
            selectedJobPosting?.title ||
            i18n.t('ats.candidates.add_to_another_job_posting.job_placeholder')
          }
          label={i18n.t('ats.candidates.add_to_another_job_posting.job')}
          onSelect={(item) => form.setFieldValue('atsJobPostingId', Number(item.value))}
          labelNotFound={i18n.t('ats.jobs.blank_row')}
          searchLabel={i18n.t('common.search')}
          loading={jobsLoading}
          data={jobs.map((job) => ({
            label: job.title,
            value: job.id,
            description: job.location?.name,
          }))}
          error={form.errors.atsJobPostingId}
        />

        <Select
          none={i18n.t('ats.candidates.add_to_another_job_posting.phase_placeholder')}
          label={i18n.t('ats.candidates.add_to_another_job_posting.phase')}
          value={form.values.atsApplicationPhaseId?.toString()}
          onChange={(value) => form.setFieldValue('atsApplicationPhaseId', Number(value))}
          loading={jobDataLoading}
          options={createOrderedOptions()}
          error={form.errors.atsApplicationPhaseId}
        />

        {candidatesAlreadyInTheJobPosting > 0 && (
          <Alert color="warning">
            <Box alignItems="flexStart" flexDirection="row" gap="s4">
              <Box flexNoShrink>
                <Icon.Medium icon="alert-circle" color="grey800" />
              </Box>
              <Text.Normal>
                {i18n.tp('ats.candidates.add_to_another_job_posting.existing_candidates', {
                  count: candidatesAlreadyInTheJobPosting,
                })}
              </Text.Normal>
            </Box>
          </Alert>
        )}
      </Box>
    </Modal>
  )
}
